import React from 'react';
import { Dropdown } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { Button, ButtonVariants, Text, Tooltip } from '@clockout-dev/design-elements';
import { EXPORT_SCOPES, EXPORT_SCOPE_KEYS, EXPORT_COPY } from '../reports.constants';
import styles from '../styles/reports.module.scss';

/**
 * Header "Download" control for the Reports screen (KAN-548): a dropdown with
 * the three server-side export scopes — All, By date range, Current page.
 *
 * "By date range" is disabled (with a tooltip saying why) when the active tab
 * has no resolved date range, instead of silently exporting everything.
 *
 * While an asynchronous export is being polled (`job`), a small live
 * indicator naming the job's tab and scope sits next to the control; the
 * control itself stays usable (`requestExport` refuses a second job with a
 * warning).
 *
 * antd `Dropdown` is used directly: design-elements has no dropdown/menu
 * component (precedent: roles/RoleListPanel, team/ActiveTeamCard).
 *
 * @param {Object} props
 * @param {(scope: string) => void} props.onSelect Receives an EXPORT_SCOPE_KEYS value.
 * @param {boolean} [props.byDateDisabled]
 * @param {boolean} [props.loading] An export request is in flight.
 * @param {boolean} [props.disabled]
 * @param {{ jobId: string, status: string, tab?: string, scope?: string, rowCount: number|null }|null} [props.job] Async job being polled.
 */
function ReportsExportMenu({ onSelect, byDateDisabled = false, loading = false, disabled = false, job = null }) {
    const items = EXPORT_SCOPES.map((scope) => {
        const isDate = scope.key === EXPORT_SCOPE_KEYS.DATE;
        const isDisabled = isDate && byDateDisabled;
        return {
            key: scope.key,
            disabled: isDisabled,
            label: isDisabled ? (
                <Tooltip title={EXPORT_COPY.BY_DATE_DISABLED_TOOLTIP} placement="left">
                    <span data-testid={`export-scope-${scope.key}`}>{scope.label}</span>
                </Tooltip>
            ) : (
                <span data-testid={`export-scope-${scope.key}`}>{scope.label}</span>
            ),
        };
    });

    return (
        <>
            {job ? (
                <Text
                    size="xs"
                    className={styles.exportJobIndicator}
                    role="status"
                    aria-live="polite"
                    data-testid="reports-export-job"
                >
                    {EXPORT_COPY.PREPARING_INDICATOR(job, job.rowCount)}
                </Text>
            ) : null}
            <Dropdown
                menu={{ items, onClick: ({ key }) => onSelect?.(key) }}
                trigger={['click']}
                disabled={disabled || loading}
                placement="bottomRight"
            >
                <Button
                    variant={ButtonVariants.SECONDARY}
                    disabled={disabled || loading}
                    aria-haspopup="menu"
                    aria-busy={loading || undefined}
                    data-testid="reports-export-trigger"
                >
                    <Text size="xs" fontWeight="semibold" className={styles.headerActionsText}>
                        {loading ? EXPORT_COPY.BUTTON_BUSY : EXPORT_COPY.BUTTON}
                    </Text>
                    <DownOutlined className={styles.headerActionsCaret} aria-hidden="true" />
                </Button>
            </Dropdown>
        </>
    );
}

export default ReportsExportMenu;
